import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DevicesApi, { DeviceIdentityParams, DeviceSnapshotDetailsResponse } from "../apis/devicesApi";

type View = { key: string; details: DeviceSnapshotDetailsResponse | null; loading: boolean; error: string | null; refreshError: string | null; refreshing: boolean };

/** One refresh controller for the fleet expansion and device chat. Responses belong to a device and operation. */
export default function useDeviceDetails(identity: DeviceIdentityParams | null) {
  const sourceType = identity?.sourceType, sourceId = identity?.sourceId, deviceId = identity?.deviceId;
  const selected = useMemo(() => sourceType && sourceId && deviceId ? { sourceType, sourceId, deviceId } : null, [sourceType, sourceId, deviceId]);
  const key = JSON.stringify([sourceType, sourceId, deviceId]);
  const activeKey = useRef(key);
  activeKey.current = key;
  const version = useRef(0);
  const [view, setView] = useState<View>({ key: "", details: null, loading: false, error: null, refreshError: null, refreshing: false });
  const current = view.key === key ? view : { key, details: null, loading: Boolean(selected), error: null, refreshError: null, refreshing: false };

  useEffect(() => {
    const request = ++version.current;
    let active = true;
    setView({ key, details: null, loading: Boolean(selected), error: null, refreshError: null, refreshing: false });
    if (selected) void DevicesApi.getDeviceDetails(selected, 288).then(details => {
      if (active && version.current === request) setView(previous => ({ ...previous, details, loading: false }));
    }).catch(error => {
      if (active && version.current === request) setView(previous => ({ ...previous, loading: false, error: error instanceof Error ? error.message : "Unable to load device details" }));
    });
    return () => { active = false; };
  }, [selected, key]);

  useEffect(() => {
    if (!selected || !current.details?.refreshing || current.refreshing) return;
    let active = true, failures = 0, delayed = false;
    const request = version.current;
    let timer: ReturnType<typeof setTimeout>;
    const valid = () => active && request === version.current && activeKey.current === key;
    const poll = async () => {
      let delay = 5000;
      try {
        const status = await DevicesApi.getDeviceRefreshStatus(selected);
        if (!valid()) return;
        if (!status || status.state === "succeeded" || status.state === "failed") {
          const details = await DevicesApi.getDeviceDetails(selected, 288);
          if (!valid()) return;
          setView(previous => ({ ...previous, details, refreshError: null }));
          if (!details.refreshing) return;
        } else {
          const nowDelayed = Date.now() - status.requested_at_ms >= 120000;
          const changed = nowDelayed !== delayed;
          delayed = nowDelayed;
          setView(previous => {
            if (previous.key !== key || !previous.details) return previous;
            if (!changed && !previous.refreshError && previous.details.refresh_status?.updated_at_ms === status.updated_at_ms && previous.details.refresh_status?.state === status.state) return previous;
            return { ...previous, refreshError: null, details: { ...previous.details, refresh_status: status, refreshing: true } };
          });
          delay = status.state === "retrying" ? Math.min(30000, Math.max(5000, (status.next_attempt_at_ms || 0) - Date.now())) : nowDelayed ? 15000 : 5000;
        }
        failures = 0;
      } catch (error) {
        if (!valid()) return;
        const status = Number((error as { response?: { status?: number } })?.response?.status);
        if ([401, 403, 404].includes(status)) {
          const now = Date.now();
          setView(previous => ({ ...previous, refreshError: null, details: previous.details && { ...previous.details, refreshing: false,
            refresh_status: { state: "failed", requested_at_ms: now, updated_at_ms: now, attempts: 0, error: { message: "This device or integration is no longer accessible.", status } } } }));
          return;
        }
        setView(previous => ({ ...previous, refreshError: "Unable to check the device refresh right now. We’ll keep trying." }));
        delay = Math.min(30000, 5000 * 2 ** Math.min(++failures, 3));
      }
      if (valid()) timer = setTimeout(poll, delay);
    };
    timer = setTimeout(poll, 5000);
    return () => { active = false; clearTimeout(timer); };
  }, [selected, key, current.details?.refreshing, current.refreshing]);

  const refresh = useCallback(async () => {
    if (!selected) return;
    const request = ++version.current;
    setView(previous => ({ ...previous, refreshing: true, refreshError: null, details: previous.details && { ...previous.details, refreshing: true } }));
    const valid = () => activeKey.current === key && request === version.current;
    try {
      const details = await DevicesApi.refreshDeviceDetails(selected, 288);
      if (valid()) setView(previous => ({ ...previous, details, refreshError: details.refresh_error?.message || null }));
    } catch (error) {
      if (valid()) setView(previous => ({ ...previous, refreshError: error instanceof Error ? error.message : "Unable to refresh device data" }));
    } finally {
      if (valid()) setView(previous => ({ ...previous, refreshing: false }));
    }
  }, [selected, key]);
  return { ...current, refresh };
}
